<template>
  <div class="article-metadata pl-0 pl-md-2 pr-2 pl-1 pt-1 pb-5">
    <DownloadButton
      v-if="fileSource && !isBlocked && !newArticleDetailsDisplay"
      :file-name="article.title"
      :file-type="fileType"
      :file-size="fileSize"
      :file-source="fileSource"
      data-test-id="DownloadButton"
    />
    <PeerReviewInformation :article="article" />
    <VersionListing :article="article" />
    <VersionHistory v-if="article.versionNote" :article="article" />
    <MetricsValues :article="article" />
    <LicenseInformation :article="article" />
    <DOIInformation :article="article" />
    <FundingInformation :article="article" />
    <CompetingInterestsInformation :article="article" />
    <EthicsInformation :article="article" />
    <KeywordTags
      v-if="article.keywords.length > 0 && !newArticleDetailsDisplay"
      :article="article"
    />
    <CommunityInformation v-if="showCommunityLink" :article="article" />
    <AssociatedConference v-if="article.event" :article="article" />
    <ShareButtons
      :article="article"
      :display-border-bottom="!newArticleDetailsDisplay"
      :buttons-id="buttonsId"
    />
    <PublicationInformation
      v-if="!newArticleDetailsDisplay"
      :article="article"
      last-item
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import mapFlags from "~/plugins/utilities/mapFlags";

import DownloadButton from "~/components/articles/components/DownloadButton.vue";
import PeerReviewInformation from "~/components/articles/components/PeerReviewInformation.vue";
import VersionListing from "~/components/articles/components/VersionListing.vue";
import VersionHistory from "~/components/articles/components/VersionHistory.vue";
import KeywordTags from "~/components/articles/components/KeywordTags.vue";
import MetricsValues from "~/components/articles/components/MetricsValues.vue";
import LicenseInformation from "~/components/articles/components/LicenseInformation.vue";
import DOIInformation from "~/components/articles/components/DOIInformation.vue";
import FundingInformation from "~/components/articles/components/FundingInformation.vue";
import CompetingInterestsInformation from "~/components/articles/components/CompetingInterestsInformation.vue";
import EthicsInformation from "~/components/articles/components/EthicsInformation.vue";
import ShareButtons from "~/components/articles/components/ShareButtons.vue";
import PublicationInformation from "~/components/articles/components/PublicationInformation.vue";
import AssociatedConference from "~/components/articles/components/AssociatedConference.vue";
import CommunityInformation from "~/components/articles/components/CommunityInformation.vue";

export default {
  name: "ArticleMetadata",
  components: {
    DownloadButton,
    VersionListing,
    VersionHistory,
    PeerReviewInformation,
    KeywordTags,
    MetricsValues,
    LicenseInformation,
    DOIInformation,
    FundingInformation,
    CompetingInterestsInformation,
    EthicsInformation,
    ShareButtons,
    PublicationInformation,
    AssociatedConference,
    CommunityInformation,
  },
  props: {
    article: { type: Object, required: true },
    buttonsId: { type: Number, default: undefined },
  },
  computed: {
    ...mapState({
      downloadButtonColor: (state) =>
        state.theme.layout.articleDetailsDownloadButtonColor,
    }),
    ...mapFlags([
      "showCommunitiesLinkInArticleDetails",
      "newArticleDetailsDisplay",
    ]),
    partner() {
      return this.$config.partner;
    },
    fileSource() {
      const { asset } = this.article || {};

      if (!asset) {
        return;
      }

      const fileType = this.fileType;

      if (fileType === "mp4") {
        return;
      }

      return asset.original ? asset.original.url : undefined;
    },
    fileType() {
      const { asset } = this.article || {};

      if (asset && asset.fileName) {
        const bits = asset.fileName.split(".");
        const lastBit = bits.length - 1;

        if (lastBit >= 0) {
          return bits[lastBit];
        }
      }

      return "";
    },
    fileSize() {
      const { asset } = this.article || {};

      if (asset && asset.fileSizeMegabytes) {
        if (asset.fileSizeMegabytes > 1) {
          return Math.floor(asset.fileSizeMegabytes).toString();
        }

        return asset.fileSizeMegabytes.toFixed(2);
      }

      return "";
    },
    isBlocked() {
      return this.article.blocked;
    },
    downloadBtnAriaLabel() {
      const { title } = this.article;
      return `Download ${title} ${this.fileType} ${
        this.fileSize ? `,${this.fileSize} megabytes` : ""
      }. Opens in a new tab`;
    },
    showCommunityLink() {
      return this.showCommunitiesLinkInArticleDetails && this.article.community;
    },
  },
  methods: {
    onDownload() {
      const url = this.fileSource;

      if (url) {
        window.open(url, "_blank");
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.article-metadata {
  background: #f5f5f5;
  border: 1px solid #dcdcdc;
  box-shadow: 1px 1px 4px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  width: 100%;
}
</style>
